import React, { useEffect, useMemo, useState } from 'react';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import {
    Loader2, Search, CheckCircle2, XCircle, RefreshCw, Eye,
    FileText, Sparkles, Filter, Calendar, Send,
} from 'lucide-react';

const VIOLET = '#7C6BEF';

const TEMPLATE_META = {
    custom: { label: 'Manual', color: '#6B7280', icon: FileText },
    auditoria_programada: { label: 'Cronograma', color: '#3B82F6', icon: Calendar },
    auditoria_resultado: { label: 'Calificación', color: '#10B981', icon: CheckCircle2 },
    recordatorio_vencimiento: { label: 'Recordatorio', color: '#E08B3C', icon: Sparkles },
    resumen_admin: { label: 'Resumen admin', color: VIOLET, icon: FileText },
};

/**
 * ClientCommunicationsPanel — tabbed section inside ClientDetail showing full
 * email history filtered by type and date range, with a "Resend" action.
 */
export default function ClientCommunicationsPanel({ clientId, onOpenComposer }) {
    const [loading, setLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [previewLog, setPreviewLog] = useState(null);
    const [resending, setResending] = useState(null); // log_id in progress

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/admin/clients/${clientId}/emails`);
            setLogs(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar comunicaciones'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientId]);

    const filtered = useMemo(() => {
        return logs.filter((l) => {
            if (filterType !== 'all' && l.template !== filterType) return false;
            if (filterStatus !== 'all' && l.status !== filterStatus) return false;
            if (dateFrom && (l.created_at || '') < dateFrom) return false;
            if (dateTo && (l.created_at || '') > `${dateTo}T23:59:59`) return false;
            if (search) {
                const q = search.toLowerCase();
                if (!(l.subject || '').toLowerCase().includes(q)
                    && !(l.to || '').toLowerCase().includes(q)) return false;
            }
            return true;
        });
    }, [logs, filterType, filterStatus, dateFrom, dateTo, search]);

    const stats = useMemo(() => {
        const sent = logs.filter((l) => l.status === 'sent').length;
        const failed = logs.filter((l) => l.status === 'failed').length;
        return { total: logs.length, sent, failed };
    }, [logs]);

    const resend = async (log) => {
        setResending(log.id);
        try {
            const r = await api.post(`/admin/emails/resend/${log.id}`);
            toast.success('Correo reenviado');
            await load();
            return r.data;
        } catch (e) {
            const msg = apiErrorMessage(e, 'No se pudo reenviar');
            toast.error(msg);
        } finally {
            setResending(null);
        }
    };

    return (
        <div className="p-6" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }} data-testid="communications-panel">
            {/* Header + KPIs */}
            <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="font-display text-lg font-bold mb-1">Comunicaciones</h3>
                    <p className="text-xs text-gray-500">
                        Historial completo de correos enviados a este cliente ·
                        <button type="button" onClick={load} className="ml-2 hover:underline inline-flex items-center gap-1" data-testid="comm-refresh-btn">
                            <RefreshCw className="w-3 h-3" /> Actualizar
                        </button>
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <span className="px-3 py-1.5 border" style={{ borderColor: 'var(--border)' }}>
                        Total <strong className="ml-1">{stats.total}</strong>
                    </span>
                    <span className="px-3 py-1.5 border text-green-700 dark:text-green-500" style={{ borderColor: 'var(--border)' }}>
                        Enviados <strong className="ml-1">{stats.sent}</strong>
                    </span>
                    <span className="px-3 py-1.5 border text-red-700 dark:text-red-500" style={{ borderColor: 'var(--border)' }}>
                        Fallidos <strong className="ml-1">{stats.failed}</strong>
                    </span>
                    {onOpenComposer && (
                        <button
                            type="button"
                            onClick={onOpenComposer}
                            className="inline-flex items-center gap-2 px-3 py-1.5 text-white font-bold"
                            style={{ background: VIOLET }}
                            data-testid="comm-new-email-btn"
                        >
                            <Send className="w-3.5 h-3.5" /> Nuevo correo
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mb-4 text-xs">
                <div className="flex items-center gap-2 px-2 py-1.5 border md:col-span-2" style={{ borderColor: 'var(--border)' }}>
                    <Search className="w-3.5 h-3.5 text-gray-400" />
                    <input
                        type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar asunto o destinatario…"
                        data-testid="comm-search-input"
                        className="w-full bg-transparent outline-none"
                    />
                </div>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
                    data-testid="comm-filter-type" className="px-2 py-1.5 border outline-none"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <option value="all">Todos los tipos</option>
                    <option value="custom">Manual</option>
                    <option value="auditoria_programada">Cronograma</option>
                    <option value="auditoria_resultado">Calificación</option>
                    <option value="recordatorio_vencimiento">Recordatorio</option>
                </select>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="comm-filter-status" className="px-2 py-1.5 border outline-none"
                    style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    <option value="all">Todos los estados</option>
                    <option value="sent">Enviado</option>
                    <option value="failed">Fallido</option>
                </select>
                <div className="flex items-center gap-1">
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                        data-testid="comm-date-from" className="w-1/2 px-2 py-1.5 border outline-none"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                        data-testid="comm-date-to" className="w-1/2 px-2 py-1.5 border outline-none"
                        style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                </div>
            </div>

            {/* List */}
            {loading ? (
                <div className="py-10 text-center text-gray-500 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Cargando…
                </div>
            ) : filtered.length === 0 ? (
                <div className="py-10 text-center text-gray-400 text-sm italic">
                    <Filter className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    {logs.length === 0 ? 'Aún no se han enviado correos a este cliente.' : 'No hay resultados con los filtros actuales.'}
                </div>
            ) : (
                <div className="overflow-x-auto" data-testid="comm-list">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                                <th className="py-2 px-2">Estado</th>
                                <th className="py-2 px-2">Tipo</th>
                                <th className="py-2 px-2">Asunto</th>
                                <th className="py-2 px-2">Destinatario</th>
                                <th className="py-2 px-2">Remitente</th>
                                <th className="py-2 px-2">Fecha</th>
                                <th className="py-2 px-2 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((l) => {
                                const tpl = TEMPLATE_META[l.template] || { label: l.template, color: '#6B7280', icon: FileText };
                                const TplIcon = tpl.icon;
                                const canResend = ['auditoria_programada', 'auditoria_resultado'].includes(l.template);
                                return (
                                    <tr key={l.id} className="border-t hover:bg-black/[.02] dark:hover:bg-white/[.04]" style={{ borderColor: 'var(--border)' }}>
                                        <td className="py-2 px-2">
                                            {l.status === 'sent'
                                                ? <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-500 text-xs font-bold"><CheckCircle2 className="w-3.5 h-3.5" /> Enviado</span>
                                                : <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-500 text-xs font-bold"><XCircle className="w-3.5 h-3.5" /> Fallido</span>}
                                        </td>
                                        <td className="py-2 px-2">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase font-bold tracking-wider" style={{ background: `${tpl.color}22`, color: tpl.color }}>
                                                <TplIcon className="w-3 h-3" /> {tpl.label}
                                            </span>
                                        </td>
                                        <td className="py-2 px-2 max-w-xs truncate" title={l.subject}>{l.subject}</td>
                                        <td className="py-2 px-2 text-xs text-gray-600 dark:text-gray-400">{l.to}</td>
                                        <td className="py-2 px-2 text-xs text-gray-600 dark:text-gray-400">{l.from || '—'}</td>
                                        <td className="py-2 px-2 text-xs text-gray-500">{formatDate(l.created_at)}</td>
                                        <td className="py-2 px-2 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => setPreviewLog(l)}
                                                data-testid={`comm-view-${l.id}`}
                                                title="Ver detalle"
                                                className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10"
                                            >
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                            {canResend && (
                                                <button
                                                    type="button"
                                                    onClick={() => resend(l)}
                                                    disabled={resending === l.id}
                                                    data-testid={`comm-resend-${l.id}`}
                                                    title="Reenviar"
                                                    className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40"
                                                >
                                                    {resending === l.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Preview drawer */}
            {previewLog && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPreviewLog(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl p-6 max-h-[calc(100vh-2rem)] overflow-y-auto" style={{ background: 'var(--card-bg)', border: `2px solid ${VIOLET}` }} data-testid="comm-preview-modal">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>Detalle del envío</div>
                                <h4 className="font-display text-lg font-black">{previewLog.subject}</h4>
                            </div>
                            <button type="button" onClick={() => setPreviewLog(null)} className="p-1 hover:bg-black/5 dark:hover:bg-white/10" data-testid="comm-preview-close">
                                <XCircle className="w-5 h-5" />
                            </button>
                        </div>
                        <dl className="space-y-2 text-sm">
                            <Row label="Estado" value={previewLog.status === 'sent' ? '✔ Enviado' : '✖ Fallido'} />
                            <Row label="Tipo" value={(TEMPLATE_META[previewLog.template] || {}).label || previewLog.template} />
                            <Row label="Destinatario" value={previewLog.to} />
                            <Row label="Remitente" value={previewLog.from || '—'} />
                            <Row label="Fecha" value={formatDate(previewLog.created_at)} />
                            {previewLog.reminder_threshold && <Row label="Umbral" value={`${previewLog.reminder_threshold} días`} />}
                            {previewLog.pdf_status && <Row label="Adjunto PDF" value={previewLog.pdf_status} />}
                            {previewLog.error && <Row label="Error" value={<span className="text-red-500">{previewLog.error}</span>} />}
                        </dl>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between gap-4 py-1 border-b" style={{ borderColor: 'var(--border)' }}>
            <dt className="text-xs uppercase font-bold text-gray-500 shrink-0">{label}</dt>
            <dd className="text-right break-all">{value}</dd>
        </div>
    );
}

function formatDate(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(iso);
        return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
        return iso;
    }
}
